/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useFixedActionBar.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom hook for managing fixed action bar layout and content padding
    to prevent content overlap with fixed bottom action bars.

 🔧 Notes:
    - Implements dynamic padding adjustment for fixed action bars
    - Prevents content overlap with fixed bottom elements
    - Supports configurable offset and conditional activation
    - Handles window resize events and layout adjustments
    - Provides smooth layout transitions and responsive behavior
    - Integrates with FixedActionBar component for consistent spacing

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useEffect } from 'react';

/**
 * Hook para agregar padding-bottom al contenido cuando hay una barra de acciones fija
 * @param hasFixedBar - Si hay una barra fija activa
 * @param offset - Offset adicional en píxeles (por defecto 64px para h-16)
 */
export const useFixedActionBar = (hasFixedBar: boolean = true, offset: number = 64) => {
  useEffect(() => {
    if (hasFixedBar) {
      // Agregar clase CSS al body para el padding
      document.body.classList.add('has-fixed-action-bar');
      document.body.style.paddingBottom = `${offset}px`;
      
      // También agregar padding al contenido principal si existe
      const mainContent = document.querySelector('main') || document.querySelector('.main-content');
      if (mainContent) {
        mainContent.style.paddingBottom = `${offset}px`;
      }
      
      return () => {
        // Limpiar la clase y el padding cuando el componente se desmonte
        document.body.classList.remove('has-fixed-action-bar');
        document.body.style.paddingBottom = '';
        
        if (mainContent) {
          mainContent.style.paddingBottom = '';
        }
      };
    }
  }, [hasFixedBar, offset]);
};

export default useFixedActionBar;
